import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader } from "lucide-react";

import { getReport } from "../api/persona";
import { usePersonaReportData } from "../context/PersonaReportDataContext";
import Chip from "./Chip";
import PersonaDetail from "./PersonaDetail";

const scoreTextColor = (score) => {
  if (score >= 80) return "text-emerald-300";
  if (score >= 60) return "text-emerald-400";
  if (score >= 50) return "text-yellow-400";
  return "text-orange-400";
};

const scoreBgColor = (score) => {
  if (score >= 80) return "bg-emerald-700";
  if (score >= 60) return "bg-emerald-500";
  if (score >= 50) return "bg-yellow-500";
  return "bg-orange-500";
};

const PersonaReport = ({ reportId, onReset }) => {
  const navigate = useNavigate();
  // Inside the report page the store supplies the report (fetched once on entry);
  // self-fetch is the fallback for any provider-less use.
  const store = usePersonaReportData();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("accessToken"));
  const [retryCount, setRetryCount] = useState(0);

  // Track the report alongside a fetchKey (reportId + retryCount) so loading/error
  // can be derived: a stale entry (different fetchKey) means we're still loading.
  const [data, setData] = useState({ fetchKey: null, report: null, error: null });
  const [selectedPersonaId, setSelectedPersonaId] = useState(null);

  useEffect(() => {
    const handleAuthChange = () => {
      const loggedIn = !!localStorage.getItem("accessToken");
      setIsLoggedIn(loggedIn);
      if (loggedIn) setRetryCount((c) => c + 1);
    };
    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  const fetchKey = `${reportId}-${retryCount}`;
  useEffect(() => {
    if (store) return undefined;
    const controller = new AbortController();
    const key = `${reportId}-${retryCount}`;
    getReport(reportId, { signal: controller.signal })
      .then((res) => setData({ fetchKey: key, report: res.data, error: null }))
      .catch(() => {
        if (controller.signal.aborted) return;
        setData({ fetchKey: key, report: null, error: "Failed to fetch report." });
      });
    return () => controller.abort();
  }, [store, reportId, retryCount]);

  let loading;
  let report;
  let error;
  if (store) {
    loading = store.report.loading;
    report = store.report.data;
    error = store.report.error ? "Failed to fetch report." : null;
  } else {
    loading = data.fetchKey !== fetchKey;
    report = loading ? null : data.report;
    error = loading ? null : data.error;
  }

  if (error) {
    return (
      <div className="text-center py-16">
        {isLoggedIn && <p className="text-red-400 mb-4">{error}</p>}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {isLoggedIn ? (
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary text-on-primary rounded-xl font-bold hover:opacity-90"
            >
              Retry
            </button>
          ) : (
            <button
              onClick={() => navigate("?auth=login")}
              className="px-6 py-2 bg-primary text-on-primary rounded-xl font-bold hover:opacity-90"
            >
              Log in to view your report
            </button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (report.status === "failed") {
    return (
      <div className="text-center py-16">
        <p className="text-red-400 mb-4">Report generation failed. Please try again.</p>
        <button
          onClick={onReset}
          className="px-6 py-2 bg-primary text-on-primary rounded-xl font-bold hover:opacity-90"
        >
          Retry
        </button>
      </div>
    );
  }

  const profile = report.business_profile ?? {};

  const selectedPersona = selectedPersonaId
    ? report.persona_results.find((r) => r.persona_id === selectedPersonaId)
    : null;

  if (selectedPersona) {
    return (
      <PersonaDetail
        result={selectedPersona}
        reportId={reportId}
        onBack={() => setSelectedPersonaId(null)}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="font-headline text-2xl font-bold">
          {profile.business_name} — Persona Visibility
        </h2>
        <p className="text-on-surface-variant text-sm mt-1">
          {profile.city} · {report.engines_used.join(", ")} · {report.persona_results.length}{" "}
          personas scanned
        </p>
      </div>

      {/* Score List */}
      <div>
        <h3 className="font-headline text-lg font-bold mb-3">
          Per-Persona Scores
          <span className="text-on-surface-variant font-normal text-sm ml-2">
            — click any persona to drill in
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {report.persona_results
            .slice()
            .sort((a, b) => b.visibility_score - a.visibility_score)
            .map((r) => (
              <button
                key={r.persona_id}
                onClick={() => setSelectedPersonaId(r.persona_id)}
                className="w-full text-left bg-surface-container-low rounded-xl p-5 border border-surface-container-high hover:border-primary/40 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{r.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-base">{r.persona_label}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${scoreBgColor(r.visibility_score)} transition-all`}
                        style={{ width: `${r.visibility_score}%` }}
                      />
                    </div>
                  </div>
                  <div
                    className={`text-2xl font-extrabold font-headline shrink-0 ${scoreTextColor(r.visibility_score)}`}
                  >
                    {r.visibility_score}
                  </div>
                </div>
                <div className="mt-3 flex gap-3 text-sm text-on-surface-variant flex-wrap">
                  <span>
                    Mentioned {r.mentioned_runs}/{r.total_runs} runs
                  </span>
                  {r.avg_rank != null && <span>· Avg rank #{r.avg_rank.toFixed(1)}</span>}
                  {r.fit_score > 0 && <Chip color="emerald">Fit {r.fit_score.toFixed(1)}/10</Chip>}
                  {r.visibility_score < 50 && <Chip color="orange">Needs attention</Chip>}
                </div>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default PersonaReport;
